import * as React from 'react';
import { createPortal } from 'react-dom';
import { Keyboard, X } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  scanFrameFeedRecipe,
  scanFrameGuideRecipe,
  scanFrameNoticeRecipe,
  scanFrameRecipe,
  scanFrameShutterRecipe,
  type ScanFrameGuideRecipeProps,
} from '../recipes/mobile.recipe';
import { Button } from './Button';
import { Center, Inline, Stack } from './Layout';
import { Text } from './Text';

export interface ScanFrameProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What the screen is for, as its name: "Scan VIN", "Scan QR code". */
  label: string;
  /** The live camera, usually a CameraFeed. It is stretched to fill the screen. */
  feed?: React.ReactNode;
  /** The guide box: long and low for a VIN plate, square for a QR tag. */
  shape?: NonNullable<ScanFrameGuideRecipeProps['shape']>;
  /** The line over the guide box. */
  hint?: React.ReactNode;
  /**
   * Shown on a light panel instead of the guide box, when the camera cannot
   * open: the app's EmptyState or Alert saying why, and what to do instead.
   */
  notice?: React.ReactNode;
  /** Leave the scanner. */
  onClose: () => void;
  /** Take the picture. Without it there is no shutter: the feed is read as it runs. */
  onCapture?: () => void;
  /** A picture is being taken: the shutter will not press again. */
  capturing?: boolean;
  /** Give up on the camera and type it instead. */
  onEnterManually?: () => void;
}

/**
 * A camera screen that frames what it reads: the feed behind a guide box,
 * close top left, and along the bottom the shutter and "Enter manually"
 * when the caller has them. A VIN is a picture the shutter takes; a QR code
 * is read off the feed as it runs, so its frame has no shutter.
 *
 *   <ScanFrame label="Scan VIN" feed={<CameraFeed ref={videoRef} />}
 *     hint="Line the VIN up inside the box" onCapture={capture}
 *     onClose={close} onEnterManually={() => setStep('manual')} />
 *
 * Dark whatever the theme, because it frames a camera feed. It covers the
 * screen from the body of the document it is in, not from where it is
 * rendered: a call's VIN sits in a sheet that slides on a transform, and a
 * fixed screen inside a transform is fixed to the sheet, not the phone. The
 * owner document rather than the global one, as BottomSheet does, so in the
 * library's phone frame it covers the phone.
 */
export const ScanFrame = React.forwardRef<HTMLDivElement, ScanFrameProps>(
  function ScanFrame(
    {
      label,
      feed,
      shape = 'plate',
      hint,
      notice,
      onClose,
      onCapture,
      capturing = false,
      onEnterManually,
      className,
      ...props
    },
    ref
  ) {
    const [body, setBody] = React.useState<HTMLElement | null>(null);
    const markOwner = React.useCallback((node: HTMLSpanElement | null) => {
      if (node) setBody(node.ownerDocument.body);
    }, []);
    const frame = (
      <div
        ref={ref}
        role="dialog"
        aria-label={label}
        className={cn(scanFrameRecipe(), className)}
        {...props}
      >
        <div className={scanFrameFeedRecipe()}>{feed}</div>
        <Inline gap={2} justify="between" className="relative px-4">
          <Button
            variant="overlay"
            size="icon"
            pill
            aria-label="Close scanner"
            onClick={onClose}
          >
            <X />
          </Button>
        </Inline>
        <Center className="relative flex-1 px-6">
          {notice ? (
            <div className={scanFrameNoticeRecipe()}>{notice}</div>
          ) : (
            <Stack gap={4} align="center" className="w-full">
              {hint ? (
                <Text variant="body-strong" tone="inherit" align="center">
                  {hint}
                </Text>
              ) : null}
              <div className={scanFrameGuideRecipe({ shape })} />
            </Stack>
          )}
        </Center>
        <Stack gap={4} align="center" className="relative px-4">
          {onCapture && !notice ? (
            <button
              type="button"
              aria-label="Take picture"
              disabled={capturing}
              onClick={onCapture}
              className={scanFrameShutterRecipe()}
            />
          ) : null}
          {onEnterManually ? (
            <Button
              variant="overlay"
              pill
              leadingIcon={<Keyboard />}
              onClick={onEnterManually}
            >
              Enter manually
            </Button>
          ) : null}
        </Stack>
      </div>
    );
    return (
      <>
        <span ref={markOwner} hidden />
        {body ? createPortal(frame, body) : null}
      </>
    );
  }
);
